"use client";

import { Suspense, useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { useAuth, storeTokens } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import type { AuthTokens, User } from "@/lib/types";
import { registerHref, safeNext } from "@/lib/nav";
import { autoRedirectTarget, discoverSso, withLoginHint, type DiscoveryOutcome } from "@/lib/ssoDiscovery";
import Turnstile from "@/components/ui/Turnstile";
import GoogleSignIn from "@/components/auth/GoogleSignIn";
import PasswordInput from "@/components/ui/PasswordInput";
import AuthBrandPanel from "@/components/auth/AuthBrandPanel";

const FAILED_ATTEMPTS_THRESHOLD = 3;

/**
 * The callback sends only a CODE, never a sentence — so the wording lives here
 * and can change without a backend deploy, and a provider's own error text
 * never reaches a URL.
 */
/** Carry ?next= through the IdP round trip, so a deep link survives it. */
function withReturnTo(startUrl: string, next: string): string {
  if (!next || next === "/dashboard") return startUrl;
  const sep = startUrl.includes("?") ? "&" : "?";
  return `${startUrl}${sep}return_to=${encodeURIComponent(next)}`;
}

const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_idp_refused:
    "Your identity provider declined that sign-in. If you think you should have access, ask your workspace admin to check you are assigned to SignForge.",
  sso_link_expired:
    "That sign-in link had already been used or had expired. Please try again.",
  sso_not_configured: "Single sign-on is not set up for that workspace.",
  sso_not_enabled: "Single sign-on is not enabled for that workspace.",
  sso_exchange_failed:
    "We could not complete the sign-in with your identity provider. Please try again.",
  sso_no_id_token:
    "Your identity provider did not return the information we need to sign you in.",
  sso_unverified:
    "We could not verify your identity provider's response, so the sign-in was refused.",
  sso_domain_conflict:
    "That email address's domain is not verified for this workspace, so it cannot sign in here. Ask your workspace admin.",
  sso_no_account:
    "You do not have an account here yet. Ask your workspace admin for an invite.",
  sso_no_email: "Your identity provider did not share an email address with us.",
  membership_suspended:
    "Your access to that workspace has been suspended. Contact your workspace admin.",
  account_suspended:
    "Your account has been suspended. Contact support@signforge.io if you think this is a mistake.",
  service_account: "That address cannot be used to sign in.",
  default: "We could not complete that sign-in. Please try again.",
};

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><p className="text-sm text-slate-400">Loading...</p></div>}>
      <LoginContent />
    </Suspense>
  );
}

function LoginContent() {
  const { login, refreshUser, isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = safeNext(searchParams.get("next"));
  const inviteToken = searchParams.get("invite_token");
  // Set by forceLogout() for the one session-ending 403 — without this the
  // user gets the same silent, unexplained redirect as an expired session.
  const suspended = searchParams.get("reason") === "suspended";

  // Redirect to destination if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.replace(next);
    }
  }, [isLoading, isAuthenticated, router, next]);

  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Email-first discovery. Nothing below the email box renders until we know
  // which workspaces, if any, federate this address: one "Sign in with …"
  // button per match, and the password field unless EVERY match requires SSO.
  const [sso, setSso] = useState<DiscoveryOutcome | null>(null);
  const [checkingSso, setCheckingSso] = useState(false);
  // "I have a personal SignForge account" — brings the password box back when
  // every matching workspace requires SSO. She may be a member of none of them.
  const [revealPassword, setRevealPassword] = useState(false);
  const ssoError = searchParams.get("sso_error");
  // /login/password redirects here with ?password=1: the owner's door past the
  // auto-redirect. Not a secret — only an owner gets past the workspace guard
  // with a password; everyone else is bounced to the IdP after signing in.
  const passwordDoor = searchParams.get("password") === "1";

  const showTurnstile = failedAttempts >= FAILED_ATTEMPTS_THRESHOLD;

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleGoogleSuccess = useCallback(
    async (idToken: string) => {
      setError("");
      setLoading(true);
      try {
        const tokens = await apiFetch<AuthTokens>("/auth/google", {
          method: "POST",
          body: JSON.stringify({
            id_token: idToken,
            ...(inviteToken ? { invite_token: inviteToken } : {}),
          }),
        });
        storeTokens(tokens, rememberMe);
        await refreshUser();
        window.sfAnalytics?.track("login", "Auth", "google");
        const me = await apiFetch<User>("/auth/me", { token: tokens.access_token });
        router.push(me.terms_accepted_at ? next : `/accept-terms?next=${encodeURIComponent(next)}`);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Google sign-in failed";
        setError(msg);
        window.sfAnalytics?.track("login_failed", "Auth", "google", undefined, { reason: msg });
      } finally {
        setLoading(false);
      }
    },
    [router, rememberMe, next, inviteToken]
  );

  function getErrorMessage(raw: string): string {
    if (raw.includes("Invalid email or password")) {
      return "The email or password you entered is incorrect.";
    }
    if (raw.includes("suspended")) {
      return "This account has been suspended. Contact support.";
    }
    if (raw.includes("Too many") || raw.includes("429")) {
      return "Too many attempts. Please wait a moment and try again.";
    }
    return raw;
  }

  async function handleContinue(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setCheckingSso(true);
    const outcome = await discoverSso(email);
    setCheckingSso(false);
    // `require_sso` means "this workspace is only enterable via SSO" — enforced
    // at the workspace boundary by deps_org._enforce_sso — not "nobody at this
    // domain may ever see a password box". So the buttons stay and the
    // password box is only hidden when EVERY match insists, with the
    // "I have a personal SignForge account" reveal beneath.
    setRevealPassword(passwordDoor);
    setSso(outcome);
    // The one case with no choice left: exactly one workspace, and it requires
    // SSO. Go straight to the IdP with the typed address as a hint. The buttons
    // still render underneath in case the navigation is slow. Never through the
    // password door, and never straight after the IdP bounced us with an error.
    const target = autoRedirectTarget(outcome, email, { passwordDoor, afterSsoError: !!ssoError });
    if (target) window.location.assign(withReturnTo(target, next));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, rememberMe);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Login failed";
      setFailedAttempts((c) => c + 1);
      setError(getErrorMessage(msg));
      setTurnstileToken(null);
      window.sfAnalytics?.track("login_failed", "Auth", "email", undefined, { reason: msg });
    } finally {
      setLoading(false);
    }
  }

  const isCredentialError = error.includes("email or password");

  const ssoWorkspaces = sso?.kind === "sso" ? sso.workspaces : [];
  const allRequired = sso?.kind === "sso" && sso.allRequired;
  // The password box shows once discovery has answered, unless every matching
  // workspace insists on SSO and she has not asked for it back.
  const showPassword = !!sso && (!allRequired || revealPassword);

  return (
    <div className="flex min-h-screen">
      <AuthBrandPanel
        headline={<>Signatures that<br /><span className="text-brand-500 italic">mean business.</span></>}
        subtitle="Trusted by teams and businesses to close agreements faster — every signature backed by a verifiable, open-standards audit trail."
      />

      {/* Right — Login form */}
      <div className="flex w-full lg:w-1/2 flex-col justify-center px-8 sm:px-16 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="lg:hidden mb-10 animate-slide-up-fade delay-0">
            <Link href="/">
              <h1 className="font-display text-2xl font-bold text-ink tracking-tight">
                Sign<span className="text-brand-600">Forge</span>
              </h1>
            </Link>
          </div>

          <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight animate-slide-up-fade delay-0">
            Welcome back
          </h2>
          <p className="mt-2 text-sm text-slate-500 animate-slide-up-fade delay-80">
            Sign in to your account to continue
          </p>

          {suspended && (
            <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              Your account has been suspended. Contact{" "}
              <a
                href="mailto:support@signforge.io"
                className="font-semibold underline underline-offset-2"
              >
                support@signforge.io
              </a>{" "}
              if you think this is a mistake.
            </div>
          )}

          {ssoError && (
            <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              {SSO_ERROR_MESSAGES[ssoError] ?? SSO_ERROR_MESSAGES.default}
            </div>
          )}

          {/* Google Sign-In */}
          <div className="mt-8 animate-slide-up-fade delay-160">
            <GoogleSignIn
              onSuccess={handleGoogleSuccess}
              onError={(msg) => setError(msg)}
              text="continue_with"
            />
          </div>

          {/* Divider */}
          <div className="mt-6 flex items-center gap-3 animate-slide-up-fade delay-240">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">or sign in with email</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          {error && (
            <div key={error} className="mt-6 rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700 animate-shake">
              {error}
              {isCredentialError && (
                <Link href="/forgot-password" className="ml-1 font-semibold text-brand-600 hover:text-brand-700 underline underline-offset-2">
                  Forgot your password?
                </Link>
              )}
              {isCredentialError && ssoWorkspaces.length > 0 && (
                // Honest, not presumptuous: she may have typo'd, or she may be
                // a JIT-created user with no password at all — for whom this
                // line is the only explanation she will ever get. Applies to
                // OPTIONAL domains too. With several matches we cannot name
                // hers, so we do not guess.
                <p className="mt-2 text-sm text-slate-600">
                  {ssoWorkspaces.length === 1
                    ? <>If you&apos;re a {ssoWorkspaces[0].name} employee, sign in with {ssoWorkspaces[0].name} instead.</>
                    : <>If you&apos;re an employee of one of these workspaces, sign in with it instead.</>}
                </p>
              )}
            </div>
          )}

          <form onSubmit={showPassword ? handleSubmit : handleContinue} className="mt-6 space-y-5">
            <div className="animate-slide-up-fade delay-320">
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@company.com"
                className="input-field"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // A corrected address is a new question: drop the previous
                  // answer so stale "Sign in with …" buttons never apply to it
                  // and the Continue button comes back.
                  if (sso) {
                    setSso(null);
                    setRevealPassword(false);
                  }
                }}
              />
            </div>

            {showPassword && (
            <div className="animate-slide-up-fade delay-400">
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <Link href="/forgot-password" className="text-xs font-medium text-brand-600 hover:text-brand-700 transition-colors link-underline">
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                className="input-field pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            )}

            {ssoWorkspaces.map((w) => (
              // One button per workspace that federates this domain, in the
              // server's (alphabetical) order. Primary when SSO is the only
              // way in everywhere; secondary beside a password box otherwise.
              <a
                key={w.slug}
                href={withReturnTo(withLoginHint(w.startUrl, email), next)}
                data-sso-workspace={w.slug}
                className={
                  allRequired
                    ? "btn-primary block w-full py-3 text-center animate-slide-up-fade"
                    : "block rounded-lg border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-700 transition-colors hover:border-brand-300 hover:text-brand-700 animate-slide-up-fade"
                }
              >
                Sign in with {w.name}
              </a>
            ))}

            {allRequired && !revealPassword && (
              <button
                type="button"
                onClick={() => setRevealPassword(true)}
                className="block w-full text-center text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                I have a personal SignForge account
              </button>
            )}

            {showPassword && (
            <div className="flex items-center animate-slide-up-fade delay-440">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                  Remember me
                </span>
              </label>
            </div>
            )}

            {showTurnstile && (
              <Turnstile
                onVerify={handleTurnstileVerify}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
                theme="light"
              />
            )}

            {(!sso || showPassword) && (
            <div className="animate-slide-up-fade delay-520">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3"
              >
                {loading || checkingSso ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                      <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
                    </svg>
                    {showPassword ? "Signing in..." : "Checking..."}
                  </span>
                ) : (
                  showPassword ? "Sign in" : "Continue"
                )}
              </button>
            </div>
            )}
          </form>

          <p className="mt-8 text-center text-sm text-slate-500 animate-slide-up-fade delay-600">
            Don&apos;t have an account?{" "}
            <Link
              href={registerHref({ next, inviteToken, email: searchParams.get("email") })}
              className="font-semibold text-brand-600 hover:text-brand-700 transition-colors link-underline"
            >
              Create one free
            </Link>
          </p>

          <p className="mt-6 text-center text-[11px] text-slate-400 flex items-center justify-center gap-1.5 animate-slide-up-fade delay-600">
            <svg className="h-3 w-3 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            256-bit encryption · ISO 27001 infrastructure · EU data residency
          </p>
        </div>
      </div>
    </div>
  );
}
